import React, { useState, useMemo } from "react";
import { Calculator, Receipt } from "lucide-react";

export default function EmiSimulator() {
  const [principal, setPrincipal] = useState(50000);
  const [tenureMonths, setTenureMonths] = useState(12);
  const [interestRate, setInterestRate] = useState(10);

  const { monthlyEmi, totalInterest, totalPayment } = useMemo(() => {
    const P = principal;
    const n = tenureMonths;
    const r = interestRate / 12 / 100;

    if (r === 0) {
      const emi = P / n;
      return {
        monthlyEmi: Math.round(emi),
        totalInterest: 0,
        totalPayment: P,
      };
    }

    const emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const total = emi * n;
    const interest = total - P;

    return {
      monthlyEmi: Math.round(emi),
      totalInterest: Math.round(interest),
      totalPayment: Math.round(total),
    };
  }, [principal, tenureMonths, interestRate]);

  const principalPercent = Math.round((principal / totalPayment) * 100);

  return (
    <div
      className="card-clean"
      style={{
        padding: "32px",
        borderRadius: "20px",
        background: "#ffffff",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "24px",
          paddingBottom: "16px",
          borderBottom: "1px solid #f1f5f9",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "10px",
              background: "rgba(37, 99, 235, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#2563eb",
            }}
          >
            <Calculator size={18} />
          </div>
          <div>
            <h4
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 700,
                fontSize: "1.1rem",
                color: "#0f172a",
                margin: 0,
              }}
            >
              Interactive EMI Calculator
            </h4>
            <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
              Simulate loan repayment schedules and digital receipts
            </span>
          </div>
        </div>

        <span className="badge-label">
          Live Calculation
        </span>
      </div>

      <div className="row align-items-center">
        {/* Sliders Column */}
        <div className="col-lg-6 mb-4 mb-lg-0">
          {/* Principal */}
          <div style={{ marginBottom: "22px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "6px",
              }}
            >
              <label style={{ fontSize: "0.85rem", color: "#334155", fontWeight: 600 }}>
                Loan Principal Amount
              </label>
              <span
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "1.05rem",
                  fontWeight: 700,
                  color: "#2563eb",
                }}
              >
                ₹{principal.toLocaleString("en-IN")}
              </span>
            </div>
            <input
              type="range"
              min="5000"
              max="300000"
              step="5000"
              value={principal}
              onChange={(e) => setPrincipal(Number(e.target.value))}
              style={{
                width: "100%",
                accentColor: "#2563eb",
                cursor: "pointer",
              }}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "0.72rem",
                color: "#94a3b8",
                marginTop: "2px",
              }}
            >
              <span>₹5,000</span>
              <span>₹3,00,000</span>
            </div>
          </div>

          {/* Tenure */}
          <div style={{ marginBottom: "22px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "6px",
              }}
            >
              <label style={{ fontSize: "0.85rem", color: "#334155", fontWeight: 600 }}>
                Loan Tenure
              </label>
              <span
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "1.05rem",
                  fontWeight: 700,
                  color: "#4f46e5",
                }}
              >
                {tenureMonths} Months
              </span>
            </div>
            <input
              type="range"
              min="3"
              max="36"
              step="1"
              value={tenureMonths}
              onChange={(e) => setTenureMonths(Number(e.target.value))}
              style={{
                width: "100%",
                accentColor: "#4f46e5",
                cursor: "pointer",
              }}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "0.72rem",
                color: "#94a3b8",
                marginTop: "2px",
              }}
            >
              <span>3 Months</span>
              <span>36 Months</span>
            </div>
          </div>

          {/* Interest Rate */}
          <div style={{ marginBottom: "12px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "6px",
              }}
            >
              <label style={{ fontSize: "0.85rem", color: "#334155", fontWeight: 600 }}>
                Interest Rate (% p.a.)
              </label>
              <span
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "1.05rem",
                  fontWeight: 700,
                  color: "#059669",
                }}
              >
                {interestRate}%
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="24"
              step="0.5"
              value={interestRate}
              onChange={(e) => setInterestRate(Number(e.target.value))}
              style={{
                width: "100%",
                accentColor: "#059669",
                cursor: "pointer",
              }}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "0.72rem",
                color: "#94a3b8",
                marginTop: "2px",
              }}
            >
              <span>0% (No Cost EMI)</span>
              <span>24% p.a.</span>
            </div>
          </div>
        </div>

        {/* Results Column */}
        <div className="col-lg-6">
          <div
            style={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "16px",
              padding: "22px",
            }}
          >
            <div
              style={{
                textAlign: "center",
                padding: "16px",
                background: "#ffffff",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                marginBottom: "16px",
              }}
            >
              <span style={{ fontSize: "0.75rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>
                Monthly Installment
              </span>
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "2rem",
                  fontWeight: 700,
                  color: "#0f172a",
                  marginTop: "2px",
                }}
              >
                ₹{monthlyEmi.toLocaleString("en-IN")}
                <span style={{ fontSize: "0.85rem", color: "#64748b", fontWeight: 400 }}> / mo</span>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "10px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  background: "#ffffff",
                  padding: "12px 14px",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <span style={{ fontSize: "0.75rem", color: "#64748b" }}>Total Interest</span>
                <div style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0f172a", marginTop: "2px" }}>
                  ₹{totalInterest.toLocaleString("en-IN")}
                </div>
              </div>

              <div
                style={{
                  background: "#ffffff",
                  padding: "12px 14px",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <span style={{ fontSize: "0.75rem", color: "#64748b" }}>Total Payment</span>
                <div style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0f172a", marginTop: "2px" }}>
                  ₹{totalPayment.toLocaleString("en-IN")}
                </div>
              </div>
            </div>

            {/* Ratio Bar */}
            <div style={{ marginBottom: "14px" }}>
              <div
                style={{
                  width: "100%",
                  height: "6px",
                  borderRadius: "999px",
                  background: "#4f46e5",
                  overflow: "hidden",
                  display: "flex",
                }}
              >
                <div style={{ width: `${principalPercent}%`, height: "100%", background: "#2563eb" }} />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "0.72rem",
                  color: "#64748b",
                  marginTop: "5px",
                }}
              >
                <span>Principal ({principalPercent}%)</span>
                <span>Interest ({100 - principalPercent}%)</span>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                color: "#059669",
                fontSize: "0.78rem",
                fontWeight: 500,
              }}
            >
              <Receipt size={14} />
              <span>Generates instant digital receipt for every installment</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
